import type { GalleryDescriptor } from './galleryMediaTypes'
import { supportsLambdaAuthorizer } from '@/services/amplify/authMode'
import { getAmplifyClient } from '@/services/amplifyClient'

/** Wire contract owned by Data (1a50fc1); business authorization stays server-side. */
export type MediaScope = {
  kind: 'SELF' | 'ORGANIZATION' | 'PROJECT'
  id: string
}
export type MediaAttachment = {
  model:
    | 'Organization'
    | 'Project'
    | 'Shop'
    | 'ProductStatus'
    | 'Catalog'
    | 'Blueprint'
    | 'ReferenceProperty'
  id: string
  field: string
}
export type MediaCreation =
  | { model: 'Organization' | 'Shop'; field: 'logo' }
  | { model: 'Project'; field: 'picture' }
  | { model: 'ProductStatus'; field: 'pictures' | 'proofPictures' }
export type ScopedGallery = GalleryDescriptor<string>
export type ScopedMediaTarget = {
  key: string
  contentType: string
  contentLength?: number
  sha256?: string
  metadata: Record<string, string>
}
export type ScopedMediaObject = ScopedMediaTarget & {
  versionId: string
  contentLength: number
}
export type ScopedMediaUploadInput = {
  scope: MediaScope
  access: 'PUBLIC' | 'PRIVATE'
  transport: 'DIRECT' | 'PROVIDER'
  contentType: string
  extension: string
  contentLength?: number
  sha256?: string
  publication?: MediaAttachment
  creation?: MediaCreation
  gallery?: ScopedGallery
  targetKey?: string
}
export type ScopedMediaImageInput = {
  itemId: string
  scope: MediaScope
  source: {
    key: string
    versionId?: string
    attachment?: MediaAttachment
    scope?: MediaScope
  }
  action: 'CUTOUT' | 'NORMALIZE' | 'THUMBNAIL' | 'LOGO_WEBP'
  gallery?: ScopedGallery
  targetKey?: string
}
export type ScopedUploadIntent = {
  itemId: string
  targetKey?: string
  object?: ScopedMediaObject
}
export type ScopedMediaPreparation = {
  state: 'READY' | 'UPLOADED'
  target: ScopedMediaTarget
  expiresInSeconds?: number
  grant?: unknown
  providerBinding?: ScopedMediaTarget
  object?: ScopedMediaObject
}

const prepareMutation = /* GraphQL */ `
  mutation PrepareScopedMediaUpload($input: ScopedMediaUploadInput!) {
    prepareMediaUpload(input: $input) {
      state
      target
      expiresInSeconds
      grant
      providerBinding
      object {
        key
        versionId
        contentType
        contentLength
        sha256
        metadata
      }
    }
  }
`
const processMutation = /* GraphQL */ `
  mutation ProcessScopedMediaImage($input: ScopedMediaImageInput!) {
    processMediaImage(input: $input) {
      key
      versionId
      contentType
      contentLength
      sha256
      metadata
    }
  }
`

function record(value: unknown): Record<string, unknown> {
  const parsed: unknown = typeof value === 'string' ? JSON.parse(value) : value
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed))
    throw new Error('Invalid scoped media response')
  return parsed as Record<string, unknown>
}
function strings(value: unknown): Record<string, string> {
  const data = record(value)
  if (!Object.values(data).every((entry) => typeof entry === 'string'))
    throw new Error('Invalid scoped media metadata')
  return data as Record<string, string>
}
const positiveSize = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value > 0
const checksum = (value: unknown): value is string =>
  typeof value === 'string' && /^[A-Za-z0-9+/]{43}=$/.test(value)
function sameMetadata(a: Record<string, string>, b: Record<string, string>) {
  return (
    Object.keys(a).length === Object.keys(b).length &&
    Object.entries(a).every(([key, value]) => b[key] === value)
  )
}
function object(value: unknown): ScopedMediaObject {
  const data = record(value)
  if (
    typeof data.key !== 'string' ||
    !data.key ||
    typeof data.versionId !== 'string' ||
    !data.versionId ||
    data.versionId === 'null' ||
    typeof data.contentType !== 'string' ||
    !data.contentType ||
    !positiveSize(data.contentLength) ||
    (data.sha256 != null && !checksum(data.sha256))
  )
    throw new Error('Invalid scoped media object')
  return {
    key: data.key,
    versionId: data.versionId,
    contentType: data.contentType,
    contentLength: data.contentLength,
    ...(data.sha256 != null ? { sha256: data.sha256 as string } : {}),
    metadata: strings(data.metadata),
  }
}
function target(
  value: unknown,
  input: ScopedMediaUploadInput,
): ScopedMediaTarget {
  const data = record(value)
  const root =
    input.access === 'PUBLIC' ? 'public/media-v2' : 'private/media-ingress-v2'
  const pattern = new RegExp(
    `^${root}/simple/${input.scope.kind.toLowerCase()}/[a-f0-9]{64}/[a-f0-9]{64}/[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\\.${input.extension}$`,
  )
  if (
    typeof data.key !== 'string' ||
    !pattern.test(data.key) ||
    (input.targetKey !== undefined && data.key !== input.targetKey) ||
    data.contentType !== input.contentType ||
    data.contentLength !== input.contentLength ||
    data.sha256 !== input.sha256
  )
    throw new Error('Invalid scoped media target')
  const metadata = strings(data.metadata)
  if (
    metadata['skop-scope-kind'] !== input.scope.kind ||
    !/^[a-f0-9]{64}$/.test(metadata['skop-scope'] ?? '') ||
    !/^[a-f0-9]{64}$/.test(metadata['skop-creator'] ?? '') ||
    (input.sha256 !== undefined && metadata['skop-sha256'] !== input.sha256)
  )
    throw new Error('Invalid scoped media provenance')
  return {
    key: data.key,
    contentType: input.contentType,
    metadata,
    ...(input.contentLength !== undefined
      ? { contentLength: input.contentLength }
      : {}),
    ...(input.sha256 !== undefined ? { sha256: input.sha256 } : {}),
  }
}
function matchesObject(value: ScopedMediaObject, expected: ScopedMediaTarget) {
  if (
    value.key !== expected.key ||
    value.contentType !== expected.contentType ||
    (expected.contentLength !== undefined &&
      value.contentLength !== expected.contentLength) ||
    (expected.sha256 !== undefined && value.sha256 !== expected.sha256) ||
    !sameMetadata(value.metadata, expected.metadata)
  )
    throw new Error('Scoped media result does not match its target')
}

export async function prepareScopedMediaUpload(
  input: ScopedMediaUploadInput,
  { assertCurrent = () => undefined }: { assertCurrent?: () => void } = {},
): Promise<ScopedMediaPreparation> {
  if (!supportsLambdaAuthorizer())
    throw new Error('Scoped media authorization unavailable')
  if (
    !['SELF', 'ORGANIZATION', 'PROJECT'].includes(input.scope.kind) ||
    !input.scope.id ||
    !/^[a-z0-9]{1,12}$/.test(input.extension)
  )
    throw new Error('Invalid scoped media request')
  assertCurrent()
  const result = await getAmplifyClient().graphql<{
    prepareMediaUpload: unknown
  }>({
    authMode: 'lambda',
    query: prepareMutation,
    variables: {
      input: {
        ...input,
        ...(input.publication
          ? { publication: JSON.stringify(input.publication) }
          : {}),
        ...(input.creation ? { creation: JSON.stringify(input.creation) } : {}),
        ...(input.gallery ? { gallery: JSON.stringify(input.gallery) } : {}),
      },
    },
  })
  if (result.errors?.length)
    throw new Error('Scoped media preparation rejected')
  assertCurrent()
  const prepared = record(result.data?.prepareMediaUpload)
  if (!['READY', 'UPLOADED'].includes(String(prepared.state)))
    throw new Error('Invalid scoped media preparation')
  const proposed = target(prepared.target, input)
  const response: ScopedMediaPreparation = {
    state: prepared.state as ScopedMediaPreparation['state'],
    target: proposed,
    ...(typeof prepared.expiresInSeconds === 'number'
      ? { expiresInSeconds: prepared.expiresInSeconds }
      : {}),
    ...(prepared.grant != null ? { grant: prepared.grant } : {}),
  }
  if (prepared.state === 'UPLOADED') {
    response.object = object(prepared.object)
    matchesObject(response.object, proposed)
  } else if (input.transport === 'PROVIDER') {
    const binding = target(prepared.providerBinding, input)
    if (
      binding.key !== proposed.key ||
      !sameMetadata(binding.metadata, proposed.metadata)
    )
      throw new Error('Invalid provider media binding')
    response.providerBinding = binding
  }
  return response
}

/** Retained on the caller's existing item draft; failure never allocates a raw fallback. */
export async function uploadScopedMedia({
  input,
  body,
  intent,
  assertCurrent,
}: {
  input: ScopedMediaUploadInput
  body: Blob
  intent: ScopedUploadIntent
  assertCurrent: () => void
}): Promise<ScopedMediaObject> {
  if (
    input.transport !== 'DIRECT' ||
    !positiveSize(input.contentLength) ||
    !checksum(input.sha256) ||
    body.size !== input.contentLength ||
    body.type.toLowerCase() !== input.contentType.toLowerCase()
  )
    throw new Error('Invalid scoped upload body')
  assertCurrent()
  const prepared = await prepareScopedMediaUpload(
    {
      ...input,
      ...(intent.targetKey ? { targetKey: intent.targetKey } : {}),
    },
    { assertCurrent },
  )
  // Keep the exact server allocation even if PUT, verification or association loses its ACK.
  intent.targetKey = prepared.target.key
  if (prepared.state === 'UPLOADED') {
    intent.object = prepared.object!
    return prepared.object!
  }
  const grant = record(prepared.grant)
  const headers = strings(grant.headers)
  const normalized = Object.fromEntries(
    Object.entries(headers).map(([key, value]) => [key.toLowerCase(), value]),
  )
  const url = new URL(String(grant.url))
  if (
    grant.method !== 'PUT' ||
    url.protocol !== 'https:' ||
    url.username ||
    url.password ||
    url.hash ||
    !/^[a-z0-9.-]+\.s3\.[a-z0-9-]+\.amazonaws\.com$/.test(url.hostname) ||
    decodeURIComponent(url.pathname) !== '/' + prepared.target.key ||
    normalized['content-type'] !== input.contentType ||
    normalized['content-length'] !== String(input.contentLength) ||
    normalized['x-amz-checksum-sha256'] !== input.sha256 ||
    normalized['if-none-match'] !== '*' ||
    !Object.entries(prepared.target.metadata).every(
      ([key, value]) => normalized['x-amz-meta-' + key] === value,
    )
  )
    throw new Error('Invalid scoped media upload grant')
  assertCurrent()
  const browserHeaders = Object.fromEntries(
    Object.entries(headers).filter(
      ([key]) => !['host', 'content-length'].includes(key.toLowerCase()),
    ),
  )
  const uploaded = await fetch(url, {
    method: 'PUT',
    body,
    headers: browserHeaders,
    credentials: 'omit',
    redirect: 'error',
    referrerPolicy: 'no-referrer',
  })
  if (!uploaded.ok && uploaded.status !== 409 && uploaded.status !== 412)
    throw new Error(`Scoped media transport rejected (${uploaded.status})`)
  assertCurrent()
  // Re-preparation reauthorizes and verifies the exact object/version. A PUT ACK alone
  // (including a collision response) is never successful business-upload proof.
  const confirmed = await prepareScopedMediaUpload(
    { ...input, targetKey: intent.targetKey },
    { assertCurrent },
  )
  if (confirmed.state !== 'UPLOADED' || !confirmed.object)
    throw new Error('Scoped media object not confirmed')
  intent.object = confirmed.object
  return confirmed.object
}

export async function processScopedMediaImage({
  input,
  assertCurrent,
}: {
  input: ScopedMediaImageInput
  assertCurrent: () => void
}): Promise<ScopedMediaObject> {
  if (!supportsLambdaAuthorizer())
    throw new Error('Scoped media authorization unavailable')
  assertCurrent()
  const result = await getAmplifyClient().graphql<{
    processMediaImage: unknown
  }>({
    authMode: 'lambda',
    query: processMutation,
    variables: {
      input: {
        ...input,
        source: JSON.stringify(input.source),
        ...(input.gallery ? { gallery: JSON.stringify(input.gallery) } : {}),
      },
    },
  })
  if (result.errors?.length) throw new Error('Scoped media processing rejected')
  assertCurrent()
  const processed = object(result.data?.processMediaImage)
  if (
    (input.targetKey && processed.key !== input.targetKey) ||
    processed.key === input.source.key ||
    !checksum(processed.sha256)
  )
    throw new Error('Invalid scoped media processing result')
  const publicSource = input.source.key.startsWith('public/')
  const root = publicSource ? 'public/' : 'private/'
  if (!processed.key.startsWith(root))
    throw new Error('Scoped media storage class changed')
  if (
    input.action === 'THUMBNAIL' &&
    /^public\/media-v2\/simple\//.test(input.source.key) &&
    processed.key !==
      'public/thumbnail/' + input.source.key.slice('public/'.length)
  )
    throw new Error('Scoped thumbnail does not match persisted reader')
  return processed
}

/** Existing public references stay strings. PRIVATE keys require their owning signed reader. */
export function mediaReference(value: ScopedMediaObject | string): string {
  const key = typeof value === 'string' ? value : value.key
  if (
    !key.startsWith('public/') ||
    /[?#\\\x00-\x20]/.test(key) ||
    key.split('/').some((part) => !part || part === '.' || part === '..')
  )
    throw new Error('Invalid public media reference')
  return key.slice('public/'.length)
}
